import { Clock, HandCoins, Info, Store } from 'lucide-vue-next'
import { Groups, JSSchema } from 'pejer/src/tables'
import { Currency, MoneyFormatter } from 'pejer/src/tables/Global'
import { ColorPalette } from 'pejer/src/tables/helpers'
import { PayoutReason } from 'pejer/src/tables/Payout'
import { Environment } from 'pejer/src/types/types'

export namespace PayoutsEmbed {
  export interface Full extends ListItem {}

  export interface ListItem {
    id: number
    payoutId: string
    platformID: string
    environment: Environment
    payoutReason: PayoutReason
    fromBankAccountNumber?: string
    toBankAccountNumber?: string
    amount: number
    currency: Currency
    shortDescription?: string
    externalObjectIdTag?: string
    payoutFee: number
    insertTime: string
    updateTime: string

    connectedAccount: ConnectedAccount
  }

  export interface ConnectedAccount {
    connectedAccountID: string
    name: string | null
    email: string | null
    payoutsEnabled: boolean
    businessName: string
    bban: string | null
    countryCode: string | null
    business_TaxID: string | null
    info: string | null
    awaitingNetsOCRAgreement: boolean | null
    netsOcrAgreementOptionActivated: boolean

    tosIsAccepted: string | null
    zimplePayPartialIsAccepted: string | null
    signature_Completed: string | null
    insertTime: string
  }
}

export const PayoutsEmbedGroups = {
  Beløp: {
    icon: HandCoins,
    color: ColorPalette.red,
    isMainGroup: true,
  },
  Konto: {
    icon: Store,
    color: ColorPalette.pink,
  },
  Utbetalingsdato: {
    icon: Clock,
    color: ColorPalette.black,
  },
  Bilagsnummer: {
    icon: Info,
    color: ColorPalette.brown,
  },
} satisfies Groups

export const PayoutsEmbedListSchema: JSSchema<PayoutsEmbed.ListItem, typeof PayoutsEmbedGroups> = {
  name: 'Payouts Embed',
  title: 'Payouts',
  groups: PayoutsEmbedGroups,
  fields: {
    id: {
      type: 'number',
      humanReadable: 'Bilagsnummer',
      meta: {
        group: 'Bilagsnummer',
        isMain: true,
      },
      inSearchSelect: true,
    },
    payoutId: {
      type: 'string',
      humanReadable: 'Utbetalings-ID',
      meta: {
        group: 'Beløp',
        hideFromTable: true,
      },
    },
    platformID: {
      type: 'string',
      humanReadable: 'Plattform-ID',
      meta: {
        group: 'Beløp',
        hideFromTable: true,
      },
    },
    environment: {
      type: 'string',
      humanReadable: 'Miljø',
      meta: {
        group: 'Beløp',
        hideFromTable: true,
      },
    },
    payoutReason: {
      type: 'string',
      humanReadable: 'Utbetalingsårsak',
      meta: {
        group: 'Beløp',
        hideFromTable: true,
      },
    },
    fromBankAccountNumber: {
      type: 'string',
      humanReadable: 'Fra konto',
      meta: {
        group: 'Beløp',
        hideFromTable: true,
      },
    },
    toBankAccountNumber: {
      type: 'string',
      humanReadable: 'Til konto',
      meta: {
        group: 'Beløp',
      },
    },
    amount: {
      type: 'number',
      humanReadable: 'Beløp',
      meta: {
        group: 'Beløp',
        isMain: true,
      },
      formatter: MoneyFormatter,
      metaType: 'money',
    },
    currency: {
      type: 'string',
      humanReadable: 'Valuta',
      meta: {
        group: 'Beløp',
        hideFromTable: true,
      },
    },
    shortDescription: {
      type: 'string',
      humanReadable: 'Beskrivelse',
      meta: {
        group: 'Beløp',
        hideFromTable: true,
      },
    },
    externalObjectIdTag: {
      type: 'string',
      humanReadable: 'Ekstern objekt-ID-tag',
      meta: {
        group: 'Beløp',
        hideFromTable: true,
      },
    },
    payoutFee: {
      type: 'number',
      humanReadable: 'Utbetalingsavgift',
      meta: {
        group: 'Beløp',
        hideFromTable: true,
      },
    },
    insertTime: {
      type: 'date',
      humanReadable: 'Utbetalingsdato',
      meta: {
        group: 'Utbetalingsdato',
      },
      isDefaultSortColumn: true,
    },
    updateTime: {
      type: 'date',
      humanReadable: 'Oppdateringstid',
      meta: {
        group: 'Utbetalingsdato',
        hideFromTable: true,
      },
    },
    connectedAccount: {
      type: 'object',
      humanReadable: 'Tilknyttet konto',
      meta: {
        group: 'Konto',
      },
      values: {
        connectedAccountID: {
          type: 'string',
          humanReadable: 'Connected Account ID',
          meta: {
            group: 'Konto',
            hideFromTable: true,
          },
        },
        name: {
          type: 'string',
          humanReadable: 'Alias navn',
          meta: {
            group: 'Konto',
            isMain: true,
          },
          inSearchSelect: true,
        },
        businessName: {
          type: 'string',
          humanReadable: 'Bedriftsnavn',
          meta: {
            group: 'Konto',
          },
        },
        bban: {
          type: 'string',
          humanReadable: 'Kontonummer',
          meta: {
            group: 'Konto',
            hideFromTable: true,
          },
          inSearchSelect: true,
        },
        countryCode: {
          type: 'string',
          humanReadable: 'Landskode',
          meta: {
            group: 'Konto',
          },
        },
        business_TaxID: {
          type: 'string',
          humanReadable: 'Organisasjonsnummer',
          meta: {
            group: 'Konto',
          },
        },
        insertTime: {
          type: 'date',
          humanReadable: 'Opprettet',
          meta: {
            group: 'Konto',
          },
        },
      },
    },
  },
}

export const PayoutsEmbedFullSchema: JSSchema<PayoutsEmbed.Full, typeof PayoutsEmbedGroups> = {
  ...(PayoutsEmbedListSchema as JSSchema<PayoutsEmbed.Full, typeof PayoutsEmbedGroups>),
  title: 'Payout',
}
